/**
 * Home Visit Patient Page (訪問予定表 患者毎 — SCR-187/188).
 * Search patients with visits in date range, grouped by patient.
 * Supports checkbox selection for PDF print and CSV export.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AppButton, AppCheckbox } from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import {
  useHomeVisitPatientSearch,
  useHomeVisitPatientMassagers,
} from '@/hooks/main/useHomeVisitPatient';
import { usePrintHomeVisitPatientMutation } from '@/hooks/main/usePrintHomeVisitPatientMutation';
import { useExportHomeVisitPatientCsvMutation } from '@/hooks/main/useExportHomeVisitPatientCsvMutation';
import { HOME_VISIT_PATIENT_SORT_MAP } from '@/constants/visit';
import { DEFAULT_PER_PAGE } from '@/constants';
import { dateValueToIso } from '@/utils/dateConverter';
import { useAppToast } from '@/hooks/useAppToast';
import HomeVisitPatientSearchForm from './components/HomeVisitPatientSearchForm';
import type { SearchFormValues } from './components/HomeVisitPatientSearchForm';
import HomeVisitPatientTable from './components/HomeVisitPatientTable';
import type { HomeVisitPatientSearchParams } from './types';

const HomeVisitPatientPage: React.FC = () => {
  const { serviceType } = useTheme();
  const sidebarOpen = useSidebarOpen();

  const [searchParams, setSearchParams] = useState<HomeVisitPatientSearchParams | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);

  const [massagerVisitRange, setMassagerVisitRange] = useState<{
    visit_datetime_from: string | null;
    visit_datetime_to: string | null;
  }>({
    visit_datetime_from: null,
    visit_datetime_to: null,
  });

  // Print option states
  const [printClinicName, setPrintClinicName] = useState(true);
  const [printClinicAddress, setPrintClinicAddress] = useState(true);

  // Keep last search form values for PDF/CSV (need date range + sort)
  const lastSearchValues = useRef<SearchFormValues | null>(null);

  const { massagers } = useHomeVisitPatientMassagers(massagerVisitRange);
  const { rows, pagination, allPatientIds, isLoading } = useHomeVisitPatientSearch(searchParams);
  const { triggerPrint, isLoading: isPrinting } = usePrintHomeVisitPatientMutation();
  const { triggerExport, isLoading: isExporting } = useExportHomeVisitPatientCsvMutation();
  const toast = useAppToast();

  const handleSearch = useCallback((values: SearchFormValues) => {
    lastSearchValues.current = values;
    const params: HomeVisitPatientSearchParams = {
      visit_date_from: dateValueToIso(values.visitDateFrom) ?? '',
      visit_date_to: dateValueToIso(values.visitDateTo) ?? '',
      search_type: values.searchRadio === '1' ? 'serial_number' : 'patient_name',
      sort_by: HOME_VISIT_PATIENT_SORT_MAP[values.sortRadio] ?? 'serial_number',
      page: 1,
      per_page: DEFAULT_PER_PAGE,
    };

    if (values.massagerRadio === '2' && values.massagerId) {
      params.massager_id = values.massagerId;
    }
    if (values.searchRadio === '1' && values.serialNumber) {
      params.serial_number = values.serialNumber;
    }
    if (values.searchRadio === '2') {
      if (values.patientName) params.patient_name = values.patientName;
      if (values.kanaInitial) params.kana_initial = values.kanaInitial;
    }

    setSearchParams(params);
    setSelectedIds(new Set());
    setHasSearched(true);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!searchParams) return;
      setSearchParams(prev => (prev ? { ...prev, page } : null));
    },
    [searchParams]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? new Set(allPatientIds) : new Set());
    },
    [allPatientIds]
  );

  const handlePrint = useCallback(() => {
    if (selectedIds.size === 0) {
      toast.warning('対象の受診者を選択してください。');
      return;
    }
    if (!lastSearchValues.current) return;
    const sv = lastSearchValues.current;
    triggerPrint({
      patient_ids: Array.from(selectedIds),
      visit_date_from: dateValueToIso(sv.visitDateFrom) ?? '',
      visit_date_to: dateValueToIso(sv.visitDateTo) ?? '',
      sort_by: HOME_VISIT_PATIENT_SORT_MAP[sv.sortRadio] ?? 'serial_number',
      print_clinic_name: printClinicName,
      print_clinic_address: printClinicAddress,
    });
  }, [selectedIds, printClinicName, printClinicAddress, triggerPrint]);

  const handleExportCsv = useCallback(() => {
    if (selectedIds.size === 0) {
      toast.warning('対象の受診者を選択してください。');
      return;
    }
    if (!lastSearchValues.current) return;
    const sv = lastSearchValues.current;
    triggerExport({
      patient_ids: Array.from(selectedIds),
      visit_date_from: dateValueToIso(sv.visitDateFrom) ?? '',
      visit_date_to: dateValueToIso(sv.visitDateTo) ?? '',
      sort_by: HOME_VISIT_PATIENT_SORT_MAP[sv.sortRadio] ?? 'serial_number',
    });
  }, [selectedIds, triggerExport]);

  const allSelected = useMemo(
    () => allPatientIds.length > 0 && allPatientIds.every(id => selectedIds.has(id)),
    [allPatientIds, selectedIds]
  );

  const totalCount = pagination?.total ?? 0;
  const hasResults = rows.length > 0;

  return (
    <div className="pb-4">
      <HomeVisitPatientSearchForm
        onSearch={handleSearch}
        onVisitDateRangeChange={setMassagerVisitRange}
        massagers={massagers}
        serviceType={serviceType}
        isLoading={isLoading}
        totalCount={totalCount}
      />

      {hasSearched ? (
        <CollapsibleCard>
          <div className="mb-2">
            <AppCheckbox checked={allSelected} onChange={e => handleSelectAll(e.target.checked)}>
              全選択
            </AppCheckbox>
          </div>

          <HomeVisitPatientTable
            data={rows}
            pagination={pagination}
            selectedIds={selectedIds}
            onSelectChange={setSelectedIds}
            onPageChange={handlePageChange}
            isLoading={isLoading}
            hasSearched={hasSearched}
            serviceType={serviceType}
          />
        </CollapsibleCard>
      ) : null}

      {/* Bottom action bar */}
      <BottomBar
        variant="action"
        visible={hasResults}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <div className="flex items-center gap-3">
            <AppCheckbox
              checked={printClinicName}
              onChange={e => setPrintClinicName(e.target.checked)}
            >
              <span className="text-white">治療院名印刷</span>
            </AppCheckbox>
            <AppCheckbox
              checked={printClinicAddress}
              onChange={e => setPrintClinicAddress(e.target.checked)}
            >
              <span className="text-white">治療院住所・電話番号印刷</span>
            </AppCheckbox>
          </div>
        }
        right={
          <div className="flex gap-3">
            <AppButton
              type="primary"
              className="rounded"
              loading={isPrinting}
              onClick={handlePrint}
            >
              一覧印刷
            </AppButton>
            <AppButton
              type="primary"
              className="rounded"
              loading={isExporting}
              onClick={handleExportCsv}
            >
              CSV出力
            </AppButton>
          </div>
        }
      />
    </div>
  );
};

export default HomeVisitPatientPage;
